import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonGroup, IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RestoreIcon from '@mui/icons-material/Restore';
import { WidgetCatalog } from './WidgetCatalog';
import type { WidgetConfig } from '@/hooks/dashboard/useDashboardWidgets';

interface DashboardToolbarActionsProps {
  isEditing: boolean;
  isSaving: boolean;
  canEdit?: boolean;
  onToggleEdit: (editing: boolean) => void;
  onSave: () => void;
  onReset: () => void;
  onAddWidget: (widget: WidgetConfig) => void;
}

export function DashboardToolbarActions({
  isEditing,
  isSaving,
  canEdit = false,
  onToggleEdit,
  onSave,
  onReset,
  onAddWidget,
}: DashboardToolbarActionsProps) {
  const { t } = useTranslation();
  const [catalogOpen, setCatalogOpen] = useState(false);

  const handleToggleEdit = useCallback(() => {
    onToggleEdit(!isEditing);
  }, [isEditing, onToggleEdit]);

  if (!canEdit) return null;

  if (isEditing) {
    return (
      <>
        <ButtonGroup size="small" variant="outlined">
          <Tooltip title={t('dashboard.addWidget')}>
            <Button onClick={() => setCatalogOpen(true)} startIcon={<AddIcon />}>
              {t('dashboard.addWidget')}
            </Button>
          </Tooltip>
          <Tooltip title={t('dashboard.resetDefaults')}>
            <Button onClick={onReset} startIcon={<RestoreIcon />}>
              {t('common.reset')}
            </Button>
          </Tooltip>
          <Tooltip title={t('dashboard.saveLayout')}>
            <Button
              onClick={onSave}
              disabled={isSaving}
              startIcon={<SaveIcon />}
              variant="contained"
            >
              {t('common.save')}
            </Button>
          </Tooltip>
          <Tooltip title={t('dashboard.cancelEditing')}>
            <Button onClick={handleToggleEdit} startIcon={<CloseIcon />}>
              {t('common.cancel')}
            </Button>
          </Tooltip>
        </ButtonGroup>
        <WidgetCatalog
          open={catalogOpen}
          onClose={() => setCatalogOpen(false)}
          onAdd={onAddWidget}
        />
      </>
    );
  }

  return (
    <Tooltip title={t('dashboard.editDashboard')}>
      <IconButton size="small" onClick={handleToggleEdit}>
        <EditIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}
